import { Controller } from "@hotwired/stimulus"

// Generyczny inline autocomplete z dropdownem i nawigacją klawiaturą.
// Wpisz minChars znaków → debounce → AJAX GET → dropdown z wynikami.
// Pola z data-autocomplete-fill="jsonKey" wypełniane automatycznie.
// Emituje event "autocomplete:select" z danymi wybranego rekordu.
// Opcjonalnie: dirty checking + checkbox "Zapisz/Aktualizuj" (targety save*).
export default class extends Controller {
  static targets = ["input", "hiddenId", "dropdown", "list",
    "saveCheckbox", "saveWrapper", "updateCheckbox", "updateWrapper"]

  static values = {
    searchUrl: String,
    minChars: { type: Number, default: 1 },
    debounce: { type: Number, default: 400 },
    displayKey: { type: String, default: "name" },
    subtitleKeys: { type: Array, default: [] },
  }

  connect() {
    this._data = []
    this._activeIndex = null
    this._timeout = null
    this._snapshot = {}
    this._originalId = this.hasHiddenIdTarget ? this.hiddenIdTarget.value : ""
    this._hadRecord = this._originalId !== ""

    if (this.hasSaveWrapperTarget || this.hasUpdateWrapperTarget) {
      this._hideSave()
      this._hideUpdate()
      this._saveSnapshot()
    }

    this._outsideClick = (e) => {
      if (!this.element.contains(e.target)) this._hide()
    }
    document.addEventListener("click", this._outsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this._outsideClick)
    if (this._timeout) clearTimeout(this._timeout)
  }

  // --- Akcje Stimulus ---

  onInput() {
    if (this._timeout) clearTimeout(this._timeout)

    const query = this.inputTarget.value.trim()
    if (query.length < this.minCharsValue) {
      this._hide()
      return
    }

    this._timeout = setTimeout(() => this._search(query), this.debounceValue)
  }

  onKeydown(event) {
    if (!this.hasDropdownTarget || this.dropdownTarget.classList.contains("hidden")) {
      if (event.key === "Enter") event.preventDefault()
      return
    }

    const items = this.listTarget.querySelectorAll("li")
    if (items.length === 0) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this._activeIndex = (this._activeIndex == null) ? 0 : Math.min(this._activeIndex + 1, items.length - 1)
      this._highlight(items)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this._activeIndex = (this._activeIndex == null) ? 0 : Math.max(this._activeIndex - 1, 0)
      this._highlight(items)
    } else if (event.key === "Enter") {
      event.preventDefault()
      if (this._activeIndex != null) this._selectByIndex(this._activeIndex)
    } else if (event.key === "Tab" && this._activeIndex != null) {
      event.preventDefault()
      this._selectByIndex(this._activeIndex)
    } else if (event.key === "Escape") {
      this._hide()
    }
  }

  select(event) {
    event.preventDefault()
    this._selectByIndex(parseInt(event.currentTarget.dataset.index))
  }

  // akcja do wywołania z blur na polach
  checkDirty() {
    this._checkDirty()
  }

  // wzajemne wykluczanie checkboxów — "Zapisz nowego" czyści ID, "Aktualizuj" przywraca
  uncheckUpdate() {
    if (this.hasUpdateCheckboxTarget) this.updateCheckboxTarget.checked = false
    if (this.hasHiddenIdTarget) this.hiddenIdTarget.value = ""
  }

  uncheckSave() {
    if (this.hasSaveCheckboxTarget) this.saveCheckboxTarget.checked = false
    if (this.hasHiddenIdTarget && this._originalId) this.hiddenIdTarget.value = this._originalId
  }

  // --- Wyszukiwanie ---

  async _search(query) {
    const url = `${this.searchUrlValue}?q=${encodeURIComponent(query)}`
    const response = await fetch(url, {
      headers: { Accept: "application/json", "X-CSRF-Token": this._csrf() },
    })
    if (!response.ok) return

    const json = await response.json()
    this._data = json.rows || []
    this._render()
  }

  // --- Rendering ---

  _render() {
    this._activeIndex = null
    if (this._data.length === 0) { this._hide(); return }

    const list = this.listTarget
    list.innerHTML = ""

    this._data.forEach((item, index) => {
      const li = document.createElement("li")
      li.className = "px-3 py-2.5 text-sm cursor-pointer hover:bg-gray-100"
      li.dataset.index = index
      li.dataset.action = "click->autocomplete#select"
      li.setAttribute("role", "option")

      const title = document.createElement("div")
      title.className = "font-medium text-gray-900"
      title.textContent = item[this.displayKeyValue] || ""
      li.appendChild(title)

      // podtytuł z dodatkowych kluczy
      if (this.subtitleKeysValue.length > 0) {
        const parts = this.subtitleKeysValue.map(k => item[k]).filter(Boolean)
        if (parts.length > 0) {
          const sub = document.createElement("div")
          sub.className = "text-xs text-gray-500"
          sub.textContent = parts.join(" · ")
          li.appendChild(sub)
        }
      }

      list.appendChild(li)
    })

    // mousedown preventDefault zapobiega blur na inpucie przy kliknięciu w dropdown
    this.dropdownTarget.onmousedown = (e) => e.preventDefault()
    this.dropdownTarget.classList.remove("hidden")
  }

  _highlight(items) {
    items.forEach((li, i) => li.classList.toggle("bg-gray-100", i === this._activeIndex))
    if (items[this._activeIndex]) items[this._activeIndex].scrollIntoView({ block: "nearest" })
  }

  _hide() {
    if (this.hasDropdownTarget) this.dropdownTarget.classList.add("hidden")
  }

  // --- Selekcja ---

  _selectByIndex(index) {
    const item = this._data[index]
    if (!item) return

    // wypełnij hidden ID
    if (this.hasHiddenIdTarget) this.hiddenIdTarget.value = item.id || ""

    // wypełnij pole input nazwą
    if (this.hasInputTarget) this.inputTarget.value = item[this.displayKeyValue] || ""

    // wypełnij pola z data-autocomplete-fill="jsonKey"
    this.element.querySelectorAll("[data-autocomplete-fill]").forEach(el => {
      const key = el.dataset.autocompleteFill
      if (key in item) el.value = item[key] ?? ""
    })

    this._hide()
    this._hadRecord = true
    this._originalId = item.id || ""

    // snapshot po wyborze (dla dirty checking)
    if (this.hasSaveWrapperTarget || this.hasUpdateWrapperTarget) {
      this._saveSnapshot()
      this._hideSave()
      this._hideUpdate()
    }

    // emituj event z danymi — inne controllery mogą nasłuchiwać
    this.dispatch("select", { detail: item, bubbles: true })
  }

  // --- Dirty checking + checkbox (opcjonalne) ---

  _saveSnapshot() {
    this._snapshot = {}
    if (this.hasInputTarget) this._snapshot._input = this.inputTarget.value
    this.element.querySelectorAll("[data-autocomplete-fill]").forEach(el => {
      this._snapshot[el.dataset.autocompleteFill] = el.value
    })
  }

  _checkDirty() {
    if (!this.hasSaveWrapperTarget && !this.hasUpdateWrapperTarget) return

    const _hasId = this.hasHiddenIdTarget && this.hiddenIdTarget.value !== ""
    const hasName = this.hasInputTarget && this.inputTarget.value.trim() !== ""

    if (this._hadRecord) {
      const dirty = this._isDirty()
      if (dirty) {
        // zmiana nazwy → oba checkboxy (nowy klient lub aktualizacja)
        // zmiana tylko innych pól → tylko aktualizacja
        const nameDirty = this._isNameDirty()
        if (nameDirty) { this._showSave() } else { this._hideSave() }
        this._showUpdate()
      } else {
        this._hideSave()
        this._hideUpdate()
      }
    } else if (hasName) {
      this._showSave()
      this._hideUpdate()
    } else {
      this._hideSave()
      this._hideUpdate()
    }
  }

  _isNameDirty() {
    if (!this.hasInputTarget || this._snapshot._input === undefined) return false
    return this.inputTarget.value !== this._snapshot._input
  }

  _isDirty() {
    if (Object.keys(this._snapshot).length === 0) return false

    const current = {}
    if (this.hasInputTarget) current._input = this.inputTarget.value
    this.element.querySelectorAll("[data-autocomplete-fill]").forEach(el => {
      current[el.dataset.autocompleteFill] = el.value
    })

    return Object.keys(this._snapshot).some(key => current[key] !== this._snapshot[key])
  }

  _showSave() {
    if (this.hasSaveWrapperTarget) this.saveWrapperTarget.classList.remove("hidden")
  }

  _hideSave() {
    if (this.hasSaveWrapperTarget) this.saveWrapperTarget.classList.add("hidden")
    if (this.hasSaveCheckboxTarget) this.saveCheckboxTarget.checked = false
  }

  _showUpdate() {
    if (this.hasUpdateWrapperTarget) this.updateWrapperTarget.classList.remove("hidden")
  }

  _hideUpdate() {
    if (this.hasUpdateWrapperTarget) this.updateWrapperTarget.classList.add("hidden")
    if (this.hasUpdateCheckboxTarget) this.updateCheckboxTarget.checked = false
  }

  _csrf() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.content : ""
  }
}
